import Button from 'components/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import {auditAuthenticationOptions, MYSQL_DEFAULT_PORT, SQL_DEFAULT_PORT} from 'common/constants';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikRadioButton from 'components/inputs/FormikRadioButton';
import FormikTextField from 'components/inputs/FormikTextField';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import Panel from 'components/Panel';
import {layoutActions} from 'features/layout/redux';
import {get} from 'lodash';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {licenseSelectors} from 'redux/license';
import {settingsSelectors, settingsActions} from 'redux/settings';
import {AuditTrailSettings, auditTrailSettingsSchema} from 'types/AuditTrailSettings';
import {DiplomatFeature} from 'types/DiplomatFeature';
import Dialog from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {enums} from 'types/enums';
import AsyncContainer from 'components/AsyncContainer';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {useFetchAuditSettings} from 'redux/settings/hooks/useFetchAuditSettings';
import FormikInverseCheckbox from 'components/inputs/FormikInverseCheckbox';
import {castToSend} from 'common/yup/castToSend';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFormContext} from 'react-hook-form';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import api from 'api';

type DialogProps = {
   onClose: () => void;
};
const SettingsAuditTrailDialog = ({onClose}: DialogProps) => {
   return (
      <Dialog
         maxWidth="md"
         header={DialogTypes.SettingsAuditTrailDialog.dialogName}
         close={onClose}
      >
         <SettingsAuditTrailForm cancel={onClose} />
      </Dialog>
   );
};

type FormProps = {
   cancel: () => void;
};

function SettingsAuditTrailForm({cancel}: FormProps) {
   const isFetching = useSelector(settingsSelectors.isFetching);
   const settings = useSelector(settingsSelectors.auditTrailSettings);
   const dispatch = useDispatch();

   useFetchAuditSettings();

   const save = (
      values: AuditTrailSettings & any,
      callbacks: FormOnSubmitHelpers<AuditTrailSettings & any>
   ) => {
      dispatch(
         settingsActions.saveAuditTrailSettings(
            castToSend(auditTrailSettingsSchema, values),
            callbacks
         )
      );
   };

   const initValues = React.useMemo(
      () => ({
         ...auditTrailSettingsSchema.cast(settings),

         archive: !settings.archive,
         archiveBy: settings.archiveByAge ? 'archiveByAge' : 'archiveByRecords',
         authentication: get(settings, 'dbSettings.useWindowsAuthentication')
            ? 'Windows'
            : 'SQL Server',
      }),
      [settings]
   );

   return (
      <AsyncContainer loading={isFetching}>
         <Formik
            initialValues={initValues}
            onSubmit={save}
            validationSchema={auditTrailSettingsSchema}
            enableReinitialize
         >
            <AuditTrailSettingsForm cancel={cancel} />
         </Formik>
      </AsyncContainer>
   );
}

function AuditTrailSettingsForm({cancel}: FormProps) {
   const {t} = useTranslation();
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const dispatch = useDispatch();
   const getValue = useFieldValueGetter<
      AuditTrailSettings & {authentication: string; archiveBy: string}
   >();

   const suspended = getValue('suspended');
   const authentication = getValue('authentication');
   const dbType = getValue('dbSettings.dbType');
   const sqlDbType = getValue('dbSettings.sqlDbType');
   const dbHost = getValue('dbSettings.dbHost');
   const dbPort = getValue('dbSettings.dbPort');
   const archive = getValue('archive');
   const archiveBy = getValue('archiveBy');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   const {setValue} = useFormContext<AuditTrailSettings>();

   const testSqlAuditDbConnection = (address: string, port: number, timeout: number) => {
      dispatch(layoutActions.testSqlAuditDbConnection(address, port, timeout));
   };

   const auditArchiveNow = () => {
      dispatch(layoutActions.auditArchiveNow());
   };

   const onSqlTypeChange = React.useCallback(
      (ev) => {
         switch (ev.target.value) {
            case enums.sqlDbType.enum.MYSQL:
               setValue('dbSettings.dbPort', MYSQL_DEFAULT_PORT);
               break;
            case enums.sqlDbType.enum.SQL_SERVER:
               setValue('dbSettings.dbPort', SQL_DEFAULT_PORT);
               break;
         }
      },
      [setValue]
   );

   const isSuspended = suspended;
   const isSQL = dbType === enums.dbType.enum.SQL;
   const isSqlServer = isSQL && sqlDbType === enums.sqlDbType.enum.SQL_SERVER;
   const isCustom = isSQL && sqlDbType === enums.sqlDbType.enum.CUSTOM;
   const isWinAuth = authentication === 'Windows';
   const isUserPass = !isSqlServer || !isWinAuth;

   return (
      <>
         {isFeatureSupported(DiplomatFeature.SQL_AUDIT_TRAIL) ? (
            <>
               <Panel name={t('Audit Settings')}>
                  <Grid container spacing={2} alignItems="center">
                     <Grid item xs={12}>
                        <FormikInverseCheckbox
                           autoFocus
                           label={t('Skip Audit Trail')}
                           name="auditRequired"
                        />
                     </Grid>
                     <Grid item xs={4}>
                        <FormikDropdown
                           name="dbSettings.dbType"
                           label={t('Audit Trail Type')}
                           options={enums.dbType.options}
                           disabled={isSuspended}
                        />
                     </Grid>
                     <Grid item xs={4}>
                        <FormikCheckbox
                           label={t('Treat Failures as Critical')}
                           name="failureCritical"
                           disabled={isSuspended}
                        />
                     </Grid>
                     <Grid item xs={4}>
                        <FormikCheckbox
                           label={t('Audit Geolocation Info')}
                           name="lookupCountryCode"
                           disabled={isSuspended}
                        />
                     </Grid>
                  </Grid>
               </Panel>
               <Panel name={t('SQL Audit Database')} className="columns">
                  <Grid container spacing={2} alignItems="center">
                     <Grid item xs={6}>
                        <FormikDropdown
                           name="dbSettings.sqlDbType"
                           label={t('SQL Database Type')}
                           onChange={onSqlTypeChange}
                           options={enums.sqlDbType.options}
                           disabled={isSuspended || !isSQL}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikTextField
                           name="dbSettings.dbName"
                           label={t('SQL Database Name')}
                           disabled={isSuspended || !isSQL}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikTextField
                           name="dbSettings.dbUser"
                           label={t('Username')}
                           disabled={isSuspended || !isSQL || !isUserPass}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikPassphrase
                           name="dbSettings.dbPassword"
                           label={t('Password')}
                           disabled={isSuspended || !isSQL || !isUserPass}
                           fetchUrl={api.getPasswordAutid}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikTextField
                           name="dbSettings.dbHost"
                           label={t('Host')}
                           disabled={isSuspended || !isSQL}
                        />
                     </Grid>
                     <Grid item container xs={6} spacing={2} alignItems="center" wrap="nowrap">
                        <Grid item xs={6}>
                           <FormikTextField
                              name="dbSettings.dbPort"
                              label={t('Port')}
                              disabled={isSuspended || !isSQL}
                              fullWidth={false}
                           />
                        </Grid>
                        <Grid item xs={6}>
                           <Button
                              type="button"
                              disabled={isSuspended || !isSQL}
                              onClick={() => testSqlAuditDbConnection(dbHost, dbPort, 0)}
                           >
                              {t('Test')}
                           </Button>
                        </Grid>
                     </Grid>
                     <Grid item xs={6}>
                        <FormikDropdown
                           name="authentication"
                           label={t('Authentication')}
                           options={auditAuthenticationOptions}
                           disabled={isSuspended || !isSQL || !isSqlServer}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikCheckbox
                           label={t('Do Not Attempt Table Creation')}
                           name="dbSettings.skipTableCreation"
                           disabled={isSuspended || !isSQL}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikTextField
                           name="dbSettings.customDriver"
                           label={t('Custom Driver')}
                           disabled={isSuspended || !isSQL || !isCustom}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikTextField
                           name="dbSettings.customUrl"
                           label={t('Custom URL')}
                           disabled={isSuspended || !isSQL || !isCustom}
                        />
                     </Grid>
                  </Grid>
               </Panel>
               <Panel name={t('SQL Audit Archive Schedule')} className="columns">
                  <Grid container alignItems="center">
                     <Grid item xs={6}>
                        <FormikCheckbox
                           name="archive"
                           label={t('Do Not Schedule')}
                           disabled={isSuspended || !isSQL}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <Button
                           type="button"
                           disabled={!isSQL || archive}
                           onClick={auditArchiveNow}
                        >
                           {t('Archive Now')}
                        </Button>
                     </Grid>
                     <Grid item xs={6}>
                        <FormikRadioButton
                           name="archiveBy"
                           value="archiveByAge"
                           disabled={!isSQL || archive}
                           label={t('Archive By Days')}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikNumberField
                           name="maxAge"
                           label={t('After')}
                           disabled={!isSQL || archive || archiveBy !== 'archiveByAge'}
                           InputProps={{
                              endAdornment: (
                                 <InputAdornment position="end">
                                    <Trans>Days</Trans>
                                 </InputAdornment>
                              ),
                           }}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikRadioButton
                           name="archiveBy"
                           value="archiveByRecords"
                           disabled={!isSQL || archive}
                           label={t('Archive By Records')}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormikNumberField
                           name="maxNumberRecords"
                           label={t('Over')}
                           disabled={!isSQL || archive || archiveBy !== 'archiveByRecords'}
                           InputProps={{
                              endAdornment: (
                                 <InputAdornment position="end">
                                    <Trans>Records</Trans>
                                 </InputAdornment>
                              ),
                           }}
                        />
                     </Grid>
                  </Grid>
               </Panel>
            </>
         ) : (
            // SQL_AUDIT_TRAIL feature not present:
            <Panel name={t('Audit Settings')}>
               <FormikInverseCheckbox
                  autoFocus
                  label={t('Skip Audit Trail')}
                  name="auditRequired"
               />
            </Panel>
         )}
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign="center">
            <FormFooterButtons cancel={cancel} />
         </Box>
         {/* {JSON.stringify(values)}
         {JSON.stringify(touched)} */}
      </>
   );
}

export default React.memo(SettingsAuditTrailDialog);
